import api from './api';
import { ApiResponse } from '../types/api';

export interface CaixaFinanceiro {
  id: number;
  nome: string;
  tipo_vinculo: 'escritorio' | 'usuario';
  escritorio_id?: number;
  usuario_id?: number;
  data_abertura: string;
  data_fechamento?: string;
  saldo_inicial: number;
  saldo_atual: number;
  moeda: string;
  status: 'aberto' | 'fechado';
  ativo: boolean;
  is_default: boolean;
  responsavel_id?: number;
  observacoes?: string;
  createdAt: string;
  updatedAt: string;
  
  // Relacionamentos
  escritorio?: {
    id: number;
    identificacao: string;
    nome_fantasia: string;
    cidade: string;
    estado: string;
  };
  usuario?: {
    id: number;
    nome: string;
    usuario: string;
  };
  responsavel?: {
    id: number;
    nome: string;
    usuario: string;
  };
}

export interface CaixaRegistro {
  id: number;
  caixa_financeiro_id: number;
  tipo: 'entrada' | 'saida';
  categoria: string;
  descricao: string;
  valor: number;
  forma_pagamento: 'dinheiro' | 'cartao_credito' | 'cartao_debito' | 'pix' | 'transferencia' | 'boleto' | 'cheque';
  coletor_tipo: 'usuario' | 'webhook';
  coletor_id?: number;
  coletor_nome?: string;
  fatura_id?: number;
  cliente_id?: number;
  despesa_id?: number;
  gateway_id?: number;
  codigo_transacao?: string;
  data_pagamento: string;
  data_registro: string;
  usuario_id?: number;
  observacoes?: string;
  
  // Relacionamentos
  usuario?: {
    id: number;
    nome: string;
    usuario: string;
  };
  cliente?: {
    id: number;
    nome: string;
  };
  gateway?: {
    id: number;
    nome: string;
    tipo?: string;
  };
  fatura?: {
    id: number;
    numero: string;
    valor: number;
  };
  despesa?: {
    id: number;
    descricao: string;
    valor: number;
  };
}

export interface CaixaEstatisticas {
  caixasPorStatus: Array<{
    status: string;
    count: number;
    total_saldo: number;
  }>;
  caixasPorTipo: Array<{
    tipo_vinculo: string;
    count: number;
  }>;
  movimentacaoHoje: Array<{
    tipo: string;
    count: number;
    total: number;
  }>;
}

export interface CaixaResumoDashboard {
  caixa: CaixaFinanceiro;
  movimentacoes_recentes: CaixaRegistro[];
  estatisticas: {
    total_entradas_mes: number;
    total_saidas_mes: number;
    saldo_anterior_mes: number;
    quantidade_movimentacoes_mes: number;
    maior_entrada: number;
    maior_saida: number;
  };
}

export interface CaixaFilters {
  page?: number;
  limit?: number;
  status?: 'aberto' | 'fechado' | 'todos';
  tipo_vinculo?: 'escritorio' | 'usuario' | 'default' | 'todos';
  ativo?: 'true' | 'false' | 'todos';
  usuario_id?: number;
  escritorio_id?: number;
  search?: string;
}

export interface MovimentacaoFilters {
  page?: number;
  limit?: number;
  tipo?: 'entrada' | 'saida' | 'todos';
  forma_pagamento?: string;
  gateway_id?: number | string;
  coletor_id?: number | string;
  data_inicio?: string;
  data_fim?: string;
}

class CaixaFinanceiroService {
  
  // Listar caixas financeiros
  async getCaixas(filters: CaixaFilters = {}): Promise<ApiResponse<CaixaFinanceiro[]> & { pagination?: { page: number; limit: number; total: number; pages: number; } }> {
    const response = await api.get('/caixa-financeiro', { params: filters });
    return response as unknown as ApiResponse<CaixaFinanceiro[]> & { pagination?: { page: number; limit: number; total: number; pages: number; } };
  }

  // Obter caixa específico com resumo
  async getCaixaById(id: number): Promise<ApiResponse<CaixaResumoDashboard>> {
    const response = await api.get(`/caixa-financeiro/${id}`);
    return response as unknown as ApiResponse<CaixaResumoDashboard>;
  }

  // Criar novo caixa
  async createCaixa(data: {
    nome: string;
    tipo_vinculo: 'escritorio' | 'usuario';
    escritorio_id?: number;
    usuario_id?: number;
    saldo_inicial?: number;
    observacoes?: string;
  }): Promise<ApiResponse<CaixaFinanceiro>> {
    const response = await api.post('/caixa-financeiro', data);
    return response as unknown as ApiResponse<CaixaFinanceiro>;
  }

  // Atualizar caixa
  async updateCaixa(id: number, data: {
    nome?: string;
    observacoes?: string;
    responsavel_id?: number;
  }): Promise<ApiResponse<CaixaFinanceiro>> {
    const response = await api.put(`/caixa-financeiro/${id}`, data);
    return response as unknown as ApiResponse<CaixaFinanceiro>;
  }

  // Fechar caixa
  async fecharCaixa(id: number): Promise<ApiResponse<CaixaFinanceiro>> {
    const response = await api.patch(`/caixa-financeiro/${id}/fechar`);
    return response as unknown as ApiResponse<CaixaFinanceiro>;
  }

  // Ativar/Desativar caixa
  async toggleCaixaStatus(id: number): Promise<ApiResponse<CaixaFinanceiro>> {
    const response = await api.patch(`/caixa-financeiro/${id}/toggle-status`);
    return response as unknown as ApiResponse<CaixaFinanceiro>;
  }

  // Obter movimentações de um caixa
  async getMovimentacoesCaixa(id: number, filters: MovimentacaoFilters = {}): Promise<ApiResponse<CaixaRegistro[]> & { pagination?: { page: number; limit: number; total: number; pages: number; } }> {
    const response = await api.get(`/caixa-financeiro/${id}/movimentacoes`, { params: filters });
    return response as unknown as ApiResponse<CaixaRegistro[]> & { pagination?: { page: number; limit: number; total: number; pages: number; } };
  }

  // Obter estatísticas gerais
  async getEstatisticas(): Promise<ApiResponse<CaixaEstatisticas>> {
    const response = await api.get('/caixa-financeiro/stats');
    return response as unknown as ApiResponse<CaixaEstatisticas>;
  }

  // Buscar escritórios para vinculação
  async getEscritorios(): Promise<ApiResponse<any[]>> {
    const response = await api.get('/escritorios');
    return response as unknown as ApiResponse<any[]>;
  }

  // Buscar usuários para vinculação
  async getUsuarios(): Promise<ApiResponse<any[]>> {
    const response = await api.get('/users');
    return response as unknown as ApiResponse<any[]>;
  }

  // Validar se pode criar caixa do tipo especificado
  async validarCriacao(tipo_vinculo: string, vinculo_id?: number): Promise<ApiResponse<{ valid: boolean; message?: string; }>> {
    const params: any = { tipo_vinculo };
    if (vinculo_id) {
      if (tipo_vinculo === 'escritorio') params.escritorio_id = vinculo_id;
      if (tipo_vinculo === 'usuario') params.usuario_id = vinculo_id;
    }
    
    const response = await api.get('/caixa-financeiro/validar-criacao', { params });
    return response as unknown as ApiResponse<{ valid: boolean; message?: string; }>;
  }
}

export const caixaFinanceiroService = new CaixaFinanceiroService();
export default caixaFinanceiroService;
